/**
 * Converts our internal storage format to Tiptap-compatible HTML.
 *
 * Storage format uses custom tags (<b>, <i>, <font name="...">, <font size="...">,
 * <color value="...">, <footnote>) and \n for paragraph breaks.
 */
export function storageToHtml(text) {
  if (!text) return "<p></p>";

  let html = text
    .replace(/<b>/gi, "<strong>")
    .replace(/<\/b>/gi, "</strong>")
    .replace(/<i>/gi, "<em>")
    .replace(/<\/i>/gi, "</em>")
    .replace(/<font\s+name="([^"]*)">/gi, (_, name) => `<span style="font-family: '${name}', serif;">`)
    .replace(/<font\s+size="([^"]*)">/gi, (_, size) => `<span style="font-size: ${size}pt;">`)
    .replace(/<color\s+value="([^"]*)">/gi, (_, color) => `<span style="color: ${color};">`)
    .replace(/<\/font>/gi, "</span>")
    .replace(/<\/color>/gi, "</span>")
    .replace(/<footnote>([\s\S]*?)<\/footnote>/gi, '<sup class="ta-footnote">[$1]</sup>');

  // Each \n becomes a separate <p> block
  return html
    .split("\n")
    .map((line) => `<p>${line || "<br/>"}</p>`)
    .join("");
}

/**
 * Converts Tiptap HTML back to our internal storage format.
 * Walks the DOM tree so it handles Tiptap's nested span output correctly.
 */
export function htmlToStorage(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const result = nodeToStorage(div);
  // Trim leading/trailing newlines produced by block conversion
  return result.replace(/^\n+/, "").replace(/\n+$/, "");
}

function nodeToStorage(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.nodeName.toLowerCase();
  const children = Array.from(node.childNodes).map(nodeToStorage).join("");

  if (tag === "p") return children + "\n";
  if (tag === "br") return "\n";
  if (tag === "strong") return `<b>${children}</b>`;
  if (tag === "em") return `<i>${children}</i>`;

  // Footnote superscript — strip the [] wrapper added by storageToHtml
  if (tag === "sup" && node.classList.contains("ta-footnote")) {
    const inner = children.replace(/^\[/, "").replace(/\]$/, "");
    return `<footnote>${inner}</footnote>`;
  }

  if (tag === "span") {
    const style = node.getAttribute("style") || "";
    let result = children;

    const colorMatch = style.match(/color:\s*(#[a-fA-F0-9]{3,8}|rgb[a-z]*\([^)]*\))/i);
    const sizeMatch = style.match(/font-size:\s*([0-9.]+)pt/i);
    // font-family may be: 'Georgia', serif  — capture just the first name
    const fontMatch = style.match(/font-family:\s*'?([^;',]+)/i);

    if (colorMatch) {
      const raw = colorMatch[1].trim();
      // Normalize RGB to hex so the storage format is always consistent
      const colorVal = raw.startsWith("rgb") ? (rgbToHex(raw) ?? raw) : raw;
      result = `<color value="${colorVal}">${result}</color>`;
    }
    if (sizeMatch) result = `<font size="${sizeMatch[1]}">${result}</font>`;
    if (fontMatch) result = `<font name="${fontMatch[1].trim()}">${result}</font>`;

    return result;
  }

  // Pass through children of any other wrapper element
  return children;
}

/** Count plain-text characters (ignoring HTML tags) for the character limit. */
export function countPlainChars(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").length;
}

/** Convert rgb(r, g, b) string to #rrggbb hex. Returns null if input is not RGB. */
function rgbToHex(rgb) {
  const m = rgb.match(/rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/i);
  if (!m) return null;
  return (
    "#" +
    [m[1], m[2], m[3]]
      .map((n) => parseInt(n, 10).toString(16).padStart(2, "0"))
      .join("")
  );
}
